import React from 'react';
import {
  Paper,
  Stack,
  Text,
  Box,
  Group,
  Tooltip,
  Popover
} from '@mantine/core';
import {
  IconChevronDown,
  IconChevronUp,
  IconInfoCircle,
  IconCoin
} from '@tabler/icons-react';
import { MealPlan, getAssignmentMealType } from '../../types/mealPlan';
import {
  aggregateMealPlanCost,
  aggregateMealPlanDineOut,
} from '../../utils/mealPlanHelpers';

const formatUsd = (n: number): string => `$${n.toFixed(2)}`;
const formatUsdRounded = (n: number): string => `$${Math.round(n)}`;

interface MealPlanSummaryProps {
  mealPlan: MealPlan;
  summaryExpanded: boolean;
  setSummaryExpanded: (expanded: boolean) => void;
  openTooltip: string | null;
  setOpenTooltip: (tooltipId: string | null) => void;
}

export const MealPlanSummary: React.FC<MealPlanSummaryProps> = ({
  mealPlan,
  summaryExpanded,
  setSummaryExpanded,
  openTooltip,
  setOpenTooltip
}) => {
  const costEstimate = aggregateMealPlanCost(mealPlan);
  // Savings compare dinner to dinner: the dine-out estimate is
  // dinner-scoped (entree-anchored counterfactual), so the home-cost side
  // of the subtraction must be too. The groceries display stays plan-wide.
  const dinnerCostEstimate = aggregateMealPlanCost(mealPlan, 'dinner');
  const dineOutEstimate = aggregateMealPlanDineOut(mealPlan);
  const hasCost = costEstimate !== null;
  const hasSavings =
    dinnerCostEstimate !== null &&
    dineOutEstimate !== null &&
    dineOutEstimate.total > dinnerCostEstimate.total.mid;
  // Savings stand on the dinner-scoped aggregate alone, so one unpriced
  // breakfast (plan-wide cost null) can't hide the dinner comparison
  const showCostRow = hasCost || hasSavings;

  if (!mealPlan.summary && !showCostRow) {
    return null;
  }

  return (
    <Paper p="md" bg="gray.0" radius="lg">
      <Stack gap="xs">
        {mealPlan.summary && (
          <>
            {/* Desktop: Show full summary */}
            <Text size="sm" c="dark.7" lh={1.5} visibleFrom="sm">
              {mealPlan.summary}
            </Text>

            {/* Mobile: Show truncated/expandable summary */}
            <Box hiddenFrom="sm">
              <Text size="sm" c="dark.7" lh={1.5}>
                {summaryExpanded
                  ? mealPlan.summary
                  : mealPlan.summary.length > 100
                    ? `${mealPlan.summary.substring(0, 135)}...`
                    : mealPlan.summary
                }
              </Text>
              {mealPlan.summary.length > 100 && (
                <Group gap="xs" mt="xs" style={{ cursor: 'pointer', width: 'fit-content' }} onClick={() => setSummaryExpanded(!summaryExpanded)}>
                  <Text size="xs" c="orange.6">
                    {summaryExpanded ? 'Show less' : 'Show more'}
                  </Text>
                  {summaryExpanded ? <IconChevronUp size={14} color="var(--mantine-color-orange-6)" /> : <IconChevronDown size={14} color="var(--mantine-color-orange-6)" />}
                </Group>
              )}
            </Box>
          </>
        )}

        {showCostRow && (() => {
          const tooltipId = 'cost-notice';
          return (
            <Popover
              position="bottom"
              withArrow
              width={280}
              opened={openTooltip === tooltipId}
            >
              <Popover.Target>
                <Group
                  gap="xs"
                  wrap="nowrap"
                  align="flex-start"
                  style={{ cursor: 'pointer', width: 'fit-content', maxWidth: '100%' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenTooltip(openTooltip === tooltipId ? null : tooltipId);
                  }}
                >
                  <IconCoin size={14} color="var(--mantine-color-teal-7)" style={{ flexShrink: 0, marginTop: 2 }} />
                  {hasSavings && dineOutEstimate && dinnerCostEstimate ? (
                    <Text size="xs" c="teal.7">
                      Save{' '}
                      <Text component="span" size="xs" fw={700}>
                        ~{formatUsdRounded(dineOutEstimate.total - dinnerCostEstimate.total.mid)}
                      </Text>
                      {' '}this week cooking dinners at home vs. dining out
                    </Text>
                  ) : costEstimate ? (
                    <Text size="xs" c="teal.7">
                      Groceries for this week{' '}
                      <Text component="span" size="xs" fw={700}>
                        ~{formatUsdRounded(costEstimate.total.mid)}
                      </Text>
                    </Text>
                  ) : null}
                </Group>
              </Popover.Target>
              <Popover.Dropdown p="sm">
                <Stack gap={4}>
                  {costEstimate && (
                    <Text size="xs">
                      <Text component="span" size="xs" fw={700}>Groceries:</Text> {formatUsd(costEstimate.total.value)}–{formatUsd(costEstimate.total.premium)}
                    </Text>
                  )}
                  {dineOutEstimate && (
                    <Text size="xs">
                      <Text component="span" size="xs" fw={700}>Dining out:</Text> ~{formatUsd(dineOutEstimate.total)} for comparable dinners
                    </Text>
                  )}
                  <Text fz={10} c="dimmed" fs="italic" mt={4}>
                    Groceries are estimated from reference Kroger store prices at value, mid, and premium product picks. Ballpark only — actual prices vary by location.
                  </Text>
                </Stack>
              </Popover.Dropdown>
            </Popover>
          );
        })()}


        {/* Substitution indicator */}
        {(() => {
          // Chronological, with slots named so a multi-slot plan never reads
          // "Monday, Monday, Monday". All-or-nothing labeling: once any
          // changed meal is a breakfast or lunch, dinners say "dinner" too,
          // otherwise "Monday breakfast, Monday lunch, Monday" leaves the
          // bare entry reading like a mystery third meal.
          const slotOrder = { breakfast: 0, lunch: 1, dinner: 2 } as const;
          const substituted = mealPlan.assignments
            .filter(assignment => assignment.substitutionStatus?.status === "completed")
            .sort(
              (a, b) =>
                a.plannedDate.localeCompare(b.plannedDate) ||
                slotOrder[getAssignmentMealType(a)] - slotOrder[getAssignmentMealType(b)]
            );
          const labelSlots = substituted.some(
            assignment => getAssignmentMealType(assignment) !== 'dinner'
          );
          const substitutedDays = substituted.map(assignment =>
            labelSlots
              ? `${assignment.plannedDay} ${getAssignmentMealType(assignment)}`
              : assignment.plannedDay
          );

          if (substitutedDays.length > 0) {
            const daysList = substitutedDays.join(', ');
            const tooltipId = 'substitution-notice';
            return (
              <Tooltip 
                label={`Recipes changed: ${daysList}`} 
                position="bottom"
                withArrow
                multiline
                opened={openTooltip === tooltipId}
                styles={{
                  tooltip: {
                    fontSize: '0.75rem',
                    lineHeight: 1.4,
                    // multiline only wraps once something bounds the width; a
                    // long substitution list otherwise stretches one line wide
                    // (280 matches the cost popover above)
                    maxWidth: 280
                  }
                }}
              >
                <Group 
                  gap="xs" 
                  style={{ cursor: 'pointer', width: 'fit-content' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenTooltip(openTooltip === tooltipId ? null : tooltipId);
                  }}
                >
                  <IconInfoCircle size={14} color="var(--mantine-color-orange-6)" />
                  <Text size="xs" c="orange.6">
                    Some recipes have been substituted
                  </Text>
                </Group>
              </Tooltip>
            );
          }
          return null;
        })()}

      </Stack>
    </Paper>
  );
};